import React from 'react';
import './KPIWidget.css';
import PileItem from './common/PileItem';

// Types for the new KPI data structure
interface KpiAggregateData {
  start_date: string;
  end_date: string;
  average_value: number;
  max_value: number;
  min_value: number;
}

interface KpiTrendData {
  aggregation: string; // day, week, month, quarter, year
  trend_data: KpiAggregateData[];
  highest_score: number;
  lowest_score: number;
  trend_percentage: number;
  period_averages?: Record<string, KpiAggregateData>;
}

interface KpiScoreData {
  label: string;
  score: string;
  score_type: string; // int, decimal, str, bool
  max_score: number;
  is_higher_better: boolean;
  threshold_type: string; // range, range_%, single, none
  threshold_low: number;
  threshold_high: number;
  description: string;
}

interface KpiInsight {
  label: string;
  insight: string;
  start_time?: string;
  end_time?: string;
}

interface KpiInsightsData {
  label: string;
  insights_data: KpiInsight[];
}

interface RelatedKpisData {
  label: string;
  related_kpis_data: KpiScoreData[];
}

interface KpiData {
  score: KpiScoreData;
  trend?: KpiTrendData;
  quick_insight?: Record<string, string>;
  related_kpis?: RelatedKpisData;
  insights?: KpiInsightsData;
}

export interface KPIWidgetData {
  score: KpiScoreData;
  trend?: KpiTrendData;
  quick_insight?: Record<string, string>;
  related_kpis?: RelatedKpisData;
  insights?: KpiInsightsData;
}

type KPIWidgetMode = 'score' | 'trend' | 'insight';

interface KPIWidgetProps {
  data: KPIWidgetData;
  mode: KPIWidgetMode;
}

// Helper function to cast score value based on score_type
const castScoreValue = (score: string, scoreType: string): number | string | boolean => {
  switch (scoreType) {
    case 'int':
      return parseInt(score, 10);
    case 'decimal':
      return parseFloat(score);
    case 'bool':
      return score.toLowerCase() === 'true';
    case 'str':
    default:
      return score;
  }
};

// Helper function to check if threshold should be applied
const shouldApplyThreshold = (thresholdType: string): boolean => {
  return thresholdType !== 'none';
};

// Helper function to get score color based on threshold type and value
const getScoreColor = (score: number | string | boolean, scoreType: string, thresholdType: string, low: number, high: number, isHigherBetter: boolean): string => {
  if (!shouldApplyThreshold(thresholdType)) {
    return 'kpi-neutral';
  }
  
  // Only apply threshold coloring for numeric types
  if (scoreType === 'str') {
    return 'kpi-neutral';
  }
  
  const numericScore = typeof score === 'number' ? score : 0;
  
  if (thresholdType === 'range' || thresholdType === 'range_%') {
    // Range threshold logic
    if (isHigherBetter) {
      if (numericScore >= high) return 'kpi-green';
      if (numericScore >= low) return 'kpi-amber';
      return 'kpi-red';
    } else {
      // Reversed logic for lower-is-better
      if (numericScore <= low) return 'kpi-green';
      if (numericScore <= high) return 'kpi-amber';
      return 'kpi-red';
    }
  } else if (thresholdType === 'single') {
    // Single threshold logic
    const threshold = low; // Both low and high should be same for single threshold
    if (isHigherBetter) {
      return numericScore >= threshold ? 'kpi-green' : 'kpi-red';
    } else {
      // Reversed logic for lower-is-better
      return numericScore <= threshold ? 'kpi-green' : 'kpi-red';
    }
  }
  
  return 'kpi-neutral';
};

const getGrowthBg = (growth: number, isHigherBetter: boolean): string => {
  if (growth === 0) return 'kpi-growth-neutral';
  
  if (isHigherBetter) {
    // Higher is better: positive growth is good, negative is bad
    if (growth > 0) return 'kpi-growth-green';
    if (growth < 0) return 'kpi-growth-red';
  } else {
    // Lower is better: negative growth is good, positive is bad
    if (growth < 0) return 'kpi-growth-green';
    if (growth > 0) return 'kpi-growth-red';
  }
  
  return 'kpi-growth-neutral';
};

const formatScore = (score: string | number | boolean, scoreType: string, maxScore?: number): string => {
  let formattedScore: string;
  
  switch (scoreType) {
    case 'int':
    case 'decimal':
      formattedScore = typeof score === 'number' ? score.toString() : String(score);
      break;
    case 'bool':
      formattedScore = score ? 'Yes' : 'No';
      break;
    case 'str':
    default:
      formattedScore = String(score);
      break;
  }
  
  // Add max score suffix for numeric types only if maxScore exists and is not zero
  if (maxScore && maxScore > 0 && (scoreType === 'int' || scoreType === 'decimal')) {
    return `${formattedScore}/${maxScore}`;
  }
  
  return formattedScore;
};

// Helper function to generate score tooltip
const getScoreTooltip = (scoreType: string, thresholdType: string, low: number, high: number): string => {
  if (!shouldApplyThreshold(thresholdType)) {
    return 'No threshold criteria applied';
  }
  
  if (scoreType === 'str') {
    return 'Text value - no threshold comparison';
  }
  
  if (thresholdType === 'range') {
    return `Threshold Range: ${low} - ${high}`;
  } else if (thresholdType === 'range_%') {
    return `Threshold Range: ${low}% - ${high}%`;

  } else if (thresholdType === 'single') {
    return `Threshold: ${low}`;
  }
  
  return 'No threshold criteria applied';
};

// Helper function to generate growth tooltip
const getGrowthTooltip = (isHigherBetter: boolean): string => {
  return isHigherBetter 
    ? 'Higher values are better'
    : 'Lower values are better';
};

const KPIWidget: React.FC<KPIWidgetProps> = ({ data, mode }) => {

  // Add null checks to prevent errors when data is not yet loaded
  if (!data) {
    console.log("data is null or undefined");
    return <div className="kpi-widget kpi-widget-loading">Loading KPI data...</div>;
  }
  
  // Check if data.score exists and has the required properties
  if (!data.score || typeof data.score !== 'object') {
    console.log("data.score is missing or not an object:", data.score);
    return <div className="kpi-widget kpi-widget-error">KPI data unavailable</div>;
  }
  
  // Check if required properties exist
  if (typeof data.score.score === 'undefined' || 
      typeof data.score.score_type === 'undefined' || 
      typeof data.score.threshold_type === 'undefined') {
    console.log("Required score properties are missing:", data.score);
    return <div className="kpi-widget kpi-widget-error">KPI data unavailable</div>;
  }
  
  try {
    const rawScore = data.score.score;
    const scoreType = data.score.score_type;
    const thresholdType = data.score.threshold_type;
    const low = data.score.threshold_low;
    const high = data.score.threshold_high;
    const maxScore = data.score.max_score;
    const isHigherBetter = data.score.is_higher_better;
    const growth = data.trend?.trend_percentage || 0;
  

    // Cast the score value based on score_type
    const castedScore = castScoreValue(rawScore, scoreType);
    const numericScore = typeof castedScore === 'number' ? castedScore : 0;

    if (mode === 'score') {
      console.log("data", data);
      console.log("label", data.score.label);
      return (
        <div className="kpi-widget kpi-widget-score">
          <div className="kpi-score-header">
            <div className="kpi-score-header-left">
              <div 
                className={`kpi-score-main ${getScoreColor(castedScore, scoreType, thresholdType, low, high, isHigherBetter)}`}
                title={getScoreTooltip(scoreType, thresholdType, low, high)}
              >
                {formatScore(castedScore, scoreType, maxScore)}
              </div>
            </div>
            <div className="kpi-score-header-right">
              <span 
                className={`kpi-growth-tag ${getGrowthBg(growth, isHigherBetter)}`}
                title={getGrowthTooltip(isHigherBetter)}
              >
                {growth !== 0 && (
                  <span className="kpi-growth-icon">
                    {growth > 0 ? '↗' : '↘'}
                  </span>
                )}
                {growth > 0 ? `+${growth}%` : `${growth}%`}
              </span>
            </div>
          </div>
          <div 
            className="kpi-score-label"
            title={data.score.description || data.score.label}
          >
            {data.score.label}
          </div>
        </div>
      );
    }

    if (mode === 'trend') {
      const trendData = data.trend?.trend_data || [];
      const period = data.trend?.period_averages || {};
      return (
        <div className="kpi-widget kpi-widget-trend">
          <div className="kpi-score-header">
            <div className="kpi-score-header-left">
              <div 
                className={`kpi-score-main ${getScoreColor(castedScore, scoreType, thresholdType, low, high, isHigherBetter)}`}
                title={getScoreTooltip(scoreType, thresholdType, low, high)}
              >
                {formatScore(castedScore, scoreType, maxScore)}
              </div>
              <div 
                className="kpi-score-label"
                title={data.score.description || data.score.label}
              >
                {data.score.label}
              </div>
            </div>
            <div className="kpi-score-header-right">
              <span 
                className={`kpi-growth-tag ${getGrowthBg(growth, isHigherBetter)}`}
                title={getGrowthTooltip(isHigherBetter)}
              >
                {growth !== 0 && (
                  <span className="kpi-growth-icon">
                    {growth > 0 ? '↗' : '↘'}
                  </span>
                )}
                {growth > 0 ? `+${growth}%` : `${growth}%`}
              </span>
              <div className="kpi-period-averages">
                <div>7d <span className="kpi-period-avg kpi-avg-7d">{period['7_day_average']?.average_value || 0}%</span></div>
                <div>1m <span className="kpi-period-avg kpi-avg-1m">{period['1_month_average']?.average_value || 0}%</span></div>
                <div>3m <span className="kpi-period-avg kpi-avg-3m">{period['3_months_average']?.average_value || 0}%</span></div>
                <div>1y <span className="kpi-period-avg kpi-avg-1y">{period['1_yr_average']?.average_value || 0}%</span></div>
              </div>
            </div>
          </div>
          <div className="kpi-trend-graph">
            {trendData.map((point: KpiAggregateData, idx: number) => (
              <div key={idx} className="kpi-trend-bar" style={{height: `${point.average_value}%`}} title={point.start_date}></div>
            ))}
          </div>
          <div className="kpi-trend-footer">
            <PileItem
              text="Highest"
              score={formatScore(data.trend?.highest_score || 0, scoreType, maxScore)}
              thresholdHigh={shouldApplyThreshold(thresholdType) ? high : 0}
              thresholdLow={shouldApplyThreshold(thresholdType) ? low : 0}
              scoreValue={data.trend?.highest_score || 0}
              maxScore={maxScore}
              icon="leaderboard"
              showIcon={true}
              iconStyle="outlined"
              title={`Highest Score: ${formatScore(data.trend?.highest_score || 0, scoreType, maxScore)}`}
              numberStyle="threshold"
            />
            <PileItem
              text="Lowest"
              score={formatScore(data.trend?.lowest_score || 0, scoreType, maxScore)}
              thresholdHigh={shouldApplyThreshold(thresholdType) ? high : 0}
              thresholdLow={shouldApplyThreshold(thresholdType) ? low : 0}
              scoreValue={data.trend?.lowest_score || 0}
              maxScore={maxScore}
              icon="trending_down"
              showIcon={true}
              iconStyle="outlined"
              title={`Lowest Score: ${formatScore(data.trend?.lowest_score || 0, scoreType, maxScore)}`}
              numberStyle="threshold"
            />
          </div>
        </div>
      );
    }

    if (mode === 'insight') {
      const relatedKpis: KpiScoreData[] = data.related_kpis?.related_kpis_data || [];
      return (
        <div className="kpi-widget kpi-widget-insight">
          <div className="kpi-insight-left">
            <div className="kpi-score-header">
              <div className="kpi-score-header-left">
                <div 
                  className={`kpi-score-main ${getScoreColor(castedScore, scoreType, thresholdType, low, high, isHigherBetter)}`}
                  title={getScoreTooltip(scoreType, thresholdType, low, high)}
                >
                  {formatScore(castedScore, scoreType, maxScore)}
                </div>
              </div>
              <div className="kpi-score-header-right">
                <span 
                  className={`kpi-growth-tag ${getGrowthBg(growth, isHigherBetter)}`}
                  title={getGrowthTooltip(isHigherBetter)}
                >
                  {growth !== 0 && (
                    <span className="kpi-growth-icon">
                      {growth > 0 ? '↗' : '↘'}
                    </span>
                  )}
                  {growth > 0 ? `+${growth}%` : `${growth}%`}
                </span>
              </div>
            </div>
            <div 
              className="kpi-score-label"
              title={data.score.description || data.score.label}
            >
              {data.score.label}
            </div>
          </div>
          <div className="kpi-insight-center">
            <div className="kpi-insight-center-title">{data.related_kpis?.label}</div>
            <div className="kpi-insight-related-list">
              {relatedKpis.map((kpi: KpiScoreData, idx: number) => {
                const kpiCastedScore = castScoreValue(kpi.score, kpi.score_type);
                const kpiScoreText = formatScore(kpiCastedScore, kpi.score_type, kpi.max_score);
                const shouldApplyKpiThreshold = shouldApplyThreshold(kpi.threshold_type);
                
                return (
                  <PileItem
                    key={idx}
                    text={kpi.label}
                    score={kpiScoreText}
                    thresholdHigh={shouldApplyKpiThreshold ? kpi.threshold_high : 0}
                    thresholdLow={shouldApplyKpiThreshold ? kpi.threshold_low : 0}
                    scoreValue={typeof kpiCastedScore === 'number' ? kpiCastedScore : 0}
                    maxScore={kpi.max_score}
                    title={`${kpi.label}: ${kpiScoreText}`}
                    numberStyle="threshold"
                  />
                );
              })}
            </div>
          </div>
          <div className="kpi-insight-right">
            <div className="kpi-insight-right-title">{data.insights?.label}</div>
            <div className="kpi-insight-related-list">
              {data.insights?.insights_data?.slice(0, 5).map((item: KpiInsight, idx: number) => (
                <PileItem
                  key={idx}
                  text={item.insight}
                  category="default"
                  icon="lightbulb"
                  showIcon={true}
                  iconStyle="outlined"
                  title={item.insight}
                />
              ))}
            </div>
          </div>
        </div>
      );
    }

    return null;
  } catch (error) {
    console.error("Error rendering KPI widget:", error);
    return <div className="kpi-widget kpi-widget-error">KPI data unavailable</div>;
  }
};

export default KPIWidget; 